import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, MoreHorizontal, Pencil } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { StatusPill, EmptyState, TableWrap, th, td, fmtDate } from "@/components/dashboard/ui";

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500";

export default function AdminArtists({ onChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ display_name: "", email: "", role: "artist", subscription_tier: "free" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/artists");
      setRows(data);
    } catch (e) {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (userId, body, msg) => {
    try {
      await api.patch(`/admin/users/${userId}`, body);
      if (msg) toast.success(msg);
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    }
  };

  const setDistribution = (u, value) =>
    patch(u.user_id, { distribution_active: value }, value ? "Distribution enabled" : "Distribution disabled");
  const approve = (u, role) => patch(u.user_id, { role, status: "active" }, `Approved as ${role}`);
  const setStatus = (u, status) => patch(u.user_id, { status }, status === "suspended" ? "Account suspended" : "Account activated");

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      display_name: u.display_name || u.name || "",
      email: u.email || "",
      role: u.role === "pending" ? "artist" : u.role,
      subscription_tier: u.subscription_tier || "free",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    const trimmed = form.display_name.trim();
    if (!trimmed) {
      toast.error("Display name is required");
      return;
    }
    if (!form.email.trim()) {
      toast.error("Email is required");
      return;
    }
    setSaving(true);
    try {
      const body = {
        display_name: trimmed,
        name: trimmed,
        subscription_tier: form.subscription_tier,
      };
      const newEmail = form.email.trim().toLowerCase();
      if (newEmail !== (editing.email || "").toLowerCase()) {
        body.email = newEmail;
      }
      if (form.role !== editing.role) body.role = form.role;
      await api.patch(`/admin/users/${editing.user_id}`, body);
      toast.success("Member updated");
      setEditing(null);
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-artists">
      <div>
        <h2 className="font-display text-2xl tracking-tight text-white">Members</h2>
        <p className="mt-1.5 text-slate-400">
          Everyone who has signed in. Approve new members, edit details and toggle Noizbloc distribution.
        </p>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>
      ) : rows.length === 0 ? (
        <EmptyState title="No members yet" desc="Once someone signs in or accepts an invite, they'll appear here." />
      ) : (
        <TableWrap>
          <thead>
            <tr>
              <th className={th}>Member</th>
              <th className={th}>Role</th>
              <th className={th}>Distribution</th>
              <th className={th}>Releases</th>
              <th className={th}>Status</th>
              <th className={th}>Joined</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_id} data-testid={`artist-row-${u.user_id}`} className="hover:bg-white/[0.02]">
                <td className={td}>
                  <div className="flex items-center gap-3">
                    {u.picture ? (
                      <img src={u.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-xs text-white">
                        {(u.display_name || u.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="text-white">{u.display_name || u.name}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className={td + " capitalize"}>
                  {u.role === "pending" ? <span className="text-amber-300">pending approval</span> : u.role}
                </td>
                <td className={td}>
                  {u.role === "pending" ? (
                    <span className="text-slate-600 text-xs">—</span>
                  ) : (
                    <div className="flex items-center gap-2.5">
                      <Switch
                        checked={!!u.distribution_active}
                        onCheckedChange={(v) => setDistribution(u, v)}
                        data-testid={`dist-toggle-${u.user_id}`}
                      />
                      <span className={`text-xs ${u.distribution_active ? "text-emerald-300" : "text-slate-500"}`} data-testid={`dist-label-${u.user_id}`}>
                        {u.distribution_active ? "Subscribed" : "Not active"}
                      </span>
                    </div>
                  )}
                </td>
                <td className={td}>{u.release_count}</td>
                <td className={td}><StatusPill status={u.status} /></td>
                <td className={td}>{fmtDate(u.created_at)}</td>
                <td className={td + " text-right"}>
                  <div className="inline-flex items-center gap-1">
                    <button
                      data-testid={`edit-user-${u.user_id}`}
                      onClick={() => openEdit(u)}
                      className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white"
                      aria-label="Edit user"
                      title="Edit user"
                    >
                      <Pencil size={16} />
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`artist-actions-${u.user_id}`} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
                          <MoreHorizontal size={18} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#0b1220] border-white/10 text-white">
                        {u.role === "pending" && (
                          <>
                            <DropdownMenuItem onClick={() => approve(u, "artist")} className="text-blue-300 focus:bg-white/10 focus:text-blue-300 cursor-pointer" data-testid={`approve-artist-${u.user_id}`}>
                              Approve as Artist
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => approve(u, "label")} className="text-blue-300 focus:bg-white/10 focus:text-blue-300 cursor-pointer" data-testid={`approve-label-${u.user_id}`}>
                              Approve as Label
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-white/10" />
                          </>
                        )}
                        <DropdownMenuItem onClick={() => openEdit(u)} className="focus:bg-white/10 cursor-pointer" data-testid={`edit-menu-${u.user_id}`}>
                          Edit details
                        </DropdownMenuItem>
                        {u.role !== "pending" && (
                          <DropdownMenuItem onClick={() => setDistribution(u, !u.distribution_active)} className="focus:bg-white/10 cursor-pointer" data-testid={`dist-menu-${u.user_id}`}>
                            {u.distribution_active ? "Disable distribution" : "Enable distribution"}
                          </DropdownMenuItem>
                        )}
                        {u.status === "active" ? (
                          <DropdownMenuItem onClick={() => setStatus(u, "suspended")} className="text-red-400 focus:bg-white/10 focus:text-red-400 cursor-pointer" data-testid={`artist-suspend-${u.user_id}`}>
                            Suspend account
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => setStatus(u, "active")} className="text-emerald-400 focus:bg-white/10 focus:text-emerald-400 cursor-pointer" data-testid={`artist-activate-${u.user_id}`}>
                            Activate account
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-[#0b1220] border-white/10 text-white sm:max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit member</DialogTitle>
            <DialogDescription className="text-slate-400">
              Update the member&apos;s public name, email or role. Changes apply immediately.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2" data-testid="edit-user-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">
                Artist / Label display name
              </label>
              <input
                data-testid="edit-user-name"
                value={form.display_name}
                onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
                placeholder="Display name"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">
                Email
              </label>
              <input
                data-testid="edit-user-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="member@example.com"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">
                Role
              </label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="edit-user-role" className="bg-white/5 border-white/10 text-white h-[50px] rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                  <SelectItem value="artist">Artist</SelectItem>
                  <SelectItem value="label">Label</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 mb-1.5 block">
                Plan (manual override)
              </label>
              <Select value={form.subscription_tier} onValueChange={(v) => setForm((f) => ({ ...f, subscription_tier: v }))}>
                <SelectTrigger data-testid="edit-user-tier" className="bg-white/5 border-white/10 text-white h-[50px] rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="artist">Artist — £19/yr</SelectItem>
                  <SelectItem value="pro">Pro — £29/yr</SelectItem>
                  <SelectItem value="label">Label — £99/yr</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1.5 text-[11px] text-slate-500">
                Manually grant a paid plan without a Stripe payment. Distribution is enabled automatically.
              </p>
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-full border border-white/15 text-white px-5 py-2.5 hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="edit-user-save"
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black font-medium px-6 py-2.5 hover:bg-slate-200 disabled:opacity-60 transition-colors"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : "Save changes"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
